import { format, formatDistanceToNowStrict, isToday, isYesterday, differenceInCalendarDays } from "date-fns";
import { fr } from "date-fns/locale";
import { formatMoney as sharedFormatMoney, formatPhone as sharedFormatPhone, type MoneyFormatOptions } from "@digisubs/shared";

// Formatting follows the approved design: "12,428 TND", "120.00 TND", "22 oct. 2026, 15:32", "+12.5%".

export function money(minor: number | null | undefined, currency: string, opts?: MoneyFormatOptions): string {
  return sharedFormatMoney(minor ?? 0, currency, opts);
}

export function moneyCompact(minor: number | null | undefined, currency: string): string {
  return sharedFormatMoney(minor ?? 0, currency, { compact: true });
}

const intFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const decFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0";
  return intFormatter.format(value);
}

export function dec(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0";
  if (digits === 2) return decFormatter.format(value);
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

/** Compact counts for big audiences: 1.2M, 450K. */
export function compact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

/** Percentage with one decimal: 38.6%. `ratio` = true when the input is 0..1. */
export function pct(value: number | null | undefined, opts: { ratio?: boolean; signed?: boolean; digits?: number } = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0%";
  const v = opts.ratio ? value * 100 : value;
  const digits = opts.digits ?? 1;
  const body = `${v.toFixed(digits)}%`;
  return opts.signed && v > 0 ? `+${body}` : body;
}

/** Delta in percent between two values, null when the previous value is 0. */
export function deltaPct(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 22 oct. 2026, 15:32 */
export function dateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  return d ? format(d, "d MMM yyyy, HH:mm", { locale: fr }) : "";
}

/** 22 oct. 2026 */
export function dateOnly(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  return d ? format(d, "d MMM yyyy", { locale: fr }) : "";
}

/** 22 oct. 15:42 (tables, current year) */
export function shortDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return format(d, sameYear ? "d MMM HH:mm" : "d MMM yyyy", { locale: fr });
}

/** 23 oct (chart axis) */
export function dayLabel(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  return d ? format(d, "d MMM", { locale: fr }).replace(".", "") : "";
}

/** Inbox style: 15:45 today, "Hier", weekday within a week, then "21 oct." */
export function inboxTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  if (isToday(d)) return format(d, "HH:mm");
  if (isYesterday(d)) return "Hier";
  if (differenceInCalendarDays(new Date(), d) < 7) return format(d, "EEEE", { locale: fr });
  return format(d, "d MMM", { locale: fr });
}

export function timeOnly(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  return d ? format(d, "HH:mm") : "";
}

/** "il y a 5 min" */
export function relative(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return `il y a ${formatDistanceToNowStrict(d, { locale: fr })}`;
}

export function phone(e164: string | null | undefined): string {
  return sharedFormatPhone(e164);
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

export function plural(count: number, singular: string, pluralForm?: string): string {
  return `${num(count)} ${count > 1 ? (pluralForm ?? `${singular}s`) : singular}`;
}
